import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DeleteStrategyModal from '../components/Strategies/DeleteStrategyModal';
import StopStrategyModal from '../components/Strategies/StopStrategyModal';
import DesktopStrategyManager from '../components/Strategies/DesktopStrategyManager';
import MobileStrategyManager from '../components/Strategies/MobileStrategyManager';
import { strategiesAPI } from '../services/api';
import { PageTransition, Spinner } from '@/components/motion';
import useContainerMobile from '../hooks/useContainerMobile';

function ActiveStrategies() {
  const navigate = useNavigate();
  const { containerRef, isMobile } = useContainerMobile();
  const [loading, setLoading] = useState(true);
  const [strategies, setStrategies] = useState([]);
  const [filteredStrategies, setFilteredStrategies] = useState([]);
  const [statusFilters, setStatusFilters] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [stopTarget, setStopTarget] = useState(null);
  const [selectedStrategyId, setSelectedStrategyId] = useState(null);
  const intervalRef = useRef(null);

  const loadStrategies = useCallback(async () => {
    try {
      const response = await strategiesAPI.getAll({ includeArchived: 'true' });
      setStrategies(response.data || []);
    } catch (err) {
      toast.error(`Failed to load strategies: ${err.message}`);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStrategies();
    intervalRef.current = setInterval(loadStrategies, 30000);
    return () => clearInterval(intervalRef.current);
  }, [loadStrategies]);

  // Filter strategies — empty selection shows all non-archived
  useEffect(() => {
    const filtered = statusFilters.length === 0
      ? strategies.filter((s) => s.status !== 'archived')
      : strategies.filter((s) => statusFilters.includes(s.status));
    setFilteredStrategies(filtered);
    // Auto-select first if current selection is not in the filtered list
    if (filtered.length > 0 && !filtered.find((s) => s.id === selectedStrategyId)) {
      setSelectedStrategyId(filtered[0].id);
    } else if (filtered.length === 0) {
      setSelectedStrategyId(null);
    }
  }, [statusFilters, strategies]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (strategyId, action, actionName) => {
    setActionLoading((prev) => ({ ...prev, [strategyId]: actionName }));
    try {
      await action(strategyId);
      await loadStrategies();
      toast.success(`Strategy ${actionName} successful`);
    } catch (err) {
      toast.error(`Failed to ${actionName} strategy: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [strategyId]: null }));
    }
  };

  const handleStopClick = (strategy) => {
    setStopTarget(strategy);
  };

  const handleStopConfirm = async () => {
    setStopTarget(null);
    await loadStrategies();
    toast.success('Strategy stopped');
  };

  const handleArchiveClick = (strategy) => {
    setArchiveTarget(strategy);
  };

  const handleArchiveConfirm = async ({ liquidate } = {}) => {
    const strategyId = archiveTarget.id;
    try {
      await strategiesAPI.archive(strategyId, { liquidate });
      setArchiveTarget(null);
      await loadStrategies();
      toast.success('Strategy archived');
    } catch (err) {
      toast.error(`Failed to archive strategy: ${err.message}`);
    }
  };

  const handleEmergencyStop = async () => {
    if (!window.confirm('⚠️ EMERGENCY STOP ALL STRATEGIES?\n\nThis will immediately stop all running strategies. Continue?')) {
      return;
    }
    try {
      await strategiesAPI.emergencyStopAll();
      await loadStrategies();
      toast.success('All strategies stopped');
    } catch (err) {
      toast.error(`Emergency stop failed: ${err.message}`);
    }
  };

  const handleChangeTradingMode = async (strategyId, currentMode) => {
    const newMode = currentMode === 'paper' ? 'live' : 'paper';
    if (newMode === 'live') {
      if (!window.confirm('⚠️ WARNING: Switch to LIVE TRADING?\n\nThis will use REAL MONEY. Are you sure?')) {
        return;
      }
    }
    setActionLoading((prev) => ({ ...prev, [strategyId]: 'mode' }));
    try {
      await strategiesAPI.changeMode(strategyId, newMode);
      await loadStrategies();
      toast.success(`Switched to ${newMode} trading`);
    } catch (err) {
      toast.error(`Failed to change trading mode: ${err.message}`);
    } finally {
      setActionLoading((prev) => ({ ...prev, [strategyId]: null }));
    }
  };

  const handleImport = useCallback(async (parsed) => {
    try {
      await strategiesAPI.create({
        name: parsed.name,
        type: parsed.type,
        provider: parsed.provider || '',
        tradingMode: parsed.trading_mode || 'paper',
        parameters: parsed.parameters,
      });
      toast.success('Strategy imported');
      loadStrategies();
    } catch (err) {
      toast.error(`Import failed: ${err.message}`);
    }
  }, [loadStrategies]);

  const getParams = (row) => {
    if (!row.parameters) return {};
    return typeof row.parameters === 'string' ? JSON.parse(row.parameters) : row.parameters;
  };

  const statusCounts = {
    all: strategies.filter((s) => s.status !== 'archived').length,
    running: strategies.filter((s) => s.status === 'running').length,
    stopped: strategies.filter((s) => s.status === 'stopped').length,
    paused: strategies.filter((s) => s.status === 'paused').length,
    error: strategies.filter((s) => s.status === 'error').length,
    archived: strategies.filter((s) => s.status === 'archived').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="h-16 w-16 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading strategies...</p>
        </div>
      </div>
    );
  }

  const viewProps = {
    strategies,
    filteredStrategies,
    statusFilters,
    setStatusFilters,
    statusCounts,
    actionLoading,
    selectedStrategyId,
    setSelectedStrategyId,
    getParams,
    handleAction,
    handleStopClick,
    handleArchiveClick,
    handleEmergencyStop,
    handleChangeTradingMode,
    handleImport,
    loadStrategies,
    navigate,
  };

  return (
    <PageTransition className="h-full flex flex-col p-3 sm:p-4 lg:p-6 gap-4 sm:gap-6">
      <div ref={containerRef} className="flex flex-col gap-4 sm:gap-6 flex-1">
        {isMobile ? (
          <MobileStrategyManager {...viewProps} />
        ) : (
          <DesktopStrategyManager {...viewProps} />
        )}
      </div>

      <StopStrategyModal
        strategy={stopTarget}
        open={!!stopTarget}
        onClose={() => setStopTarget(null)}
        onConfirm={handleStopConfirm}
      />

      <DeleteStrategyModal
        strategy={archiveTarget}
        open={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchiveConfirm}
        mode="archive"
      />
    </PageTransition>
  );
}

export default ActiveStrategies;
